import React, { useState, useMemo } from "react";
import {
  Box,
  Avatar,
  Badge,
  Chip,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { stringToColor, getUserDisplayName, getUserInitials } from "../utils/avatarUtils";

export default function AnnotatorField({
  parentLevelAnnotators = [],  // array of parent-level annotators (read-only)
  currentLevelAnnotators = [], // array of current-level annotators (editable)
  fileLevelAnnotators = [],    // array of file-level annotators (read-only)
  allUsers = [],              // list of all user objects
  isAdmin = false,            // boolean flag for admin - now only controls editing, not display
  onChange                    // function(user, isAdd)
}) {
  const [anchor, setAnchor] = useState(null);
  const [search, setSearch] = useState("");

  // Combine parent and current level annotators for display logic with deduplication
  // COMMENTED OUT: file-level annotators functionality
  const allAnnotators = useMemo(() => {
    const parentArray = Array.isArray(parentLevelAnnotators) ? parentLevelAnnotators : [];
    const currentArray = Array.isArray(currentLevelAnnotators) ? currentLevelAnnotators : [];
    // const fileArray = Array.isArray(fileLevelAnnotators) ? fileLevelAnnotators : [];
    
    // Combine and deduplicate by email
    const combined = [...parentArray, ...currentArray]; // , ...fileArray
    const seen = new Set();
    const deduplicated = combined.filter(annotator => {
      if (seen.has(annotator.email)) {
        return false;
      }
      seen.add(annotator.email);
      return true;
    });
    
    return deduplicated;
  }, [parentLevelAnnotators, currentLevelAnnotators]); // , fileLevelAnnotators

  // COMMENTED OUT: Create combined direct annotators (current + file level) excluding parent level
  // Now only shows direct folder annotators, no file-level
  const directAnnotators = useMemo(() => {
    const parentArray = Array.isArray(parentLevelAnnotators) ? parentLevelAnnotators : [];
    const currentArray = Array.isArray(currentLevelAnnotators) ? currentLevelAnnotators : [];
    // const fileArray = Array.isArray(fileLevelAnnotators) ? fileLevelAnnotators : [];
    
    // Create a set of parent-level emails for exclusion
    const parentEmails = new Set(parentArray.map(annotator => annotator.email));
    
    // Filter only current level annotators to exclude parent level
    const filteredCurrent = currentArray.filter(annotator => !parentEmails.has(annotator.email));
    // const filteredFile = fileArray.filter(annotator => !parentEmails.has(annotator.email));
    
    // Only add direct folder assignments
    return filteredCurrent.map(annotator => ({ ...annotator, _assignmentType: 'direct' }));
    
    // COMMENTED OUT: file-level logic
    // const combined = [];
    // const seenEmails = new Set();
    // 
    // // Add current level first (higher priority)
    // filteredCurrent.forEach(annotator => {
    //   if (!seenEmails.has(annotator.email)) {
    //     combined.push({ ...annotator, _assignmentType: 'direct' });
    //     seenEmails.add(annotator.email);
    //   }
    // });
    // 
    // // Add file level only if not already present
    // filteredFile.forEach(annotator => {
    //   if (!seenEmails.has(annotator.email)) {
    //     combined.push({ ...annotator, _assignmentType: 'file' });
    //     seenEmails.add(annotator.email);
    //   }
    // });
    // 
    // return combined;
  }, [parentLevelAnnotators, currentLevelAnnotators]); // , fileLevelAnnotators

  // COMMENTED OUT: Filter only direct folder annotators for editing (exclude file-level)
  // Now all directAnnotators are editable since we removed file-level
  const editableDirectAnnotators = useMemo(() => {
    return directAnnotators; // .filter(annotator => annotator._assignmentType === 'direct');
  }, [directAnnotators]);

  const available = useMemo(() => {
    if (!isAdmin) return [];
    const usersList = Array.isArray(allUsers) ? allUsers : [];
    const q = search.trim().toLowerCase();
    const unassigned = usersList.filter(u =>
      !allAnnotators.some(a => a.email === u.email)
    );
    if (!q) return unassigned.slice(0, 100);
    return unassigned
      .filter(u => {
        const name = (u.name || "").toLowerCase();
        const email = (u.email || "").toLowerCase();
        const username = (u.username || "").toLowerCase();
        return name.includes(q) || email.includes(q) || username.includes(q);
      })
      .slice(0, 100);
  }, [allUsers, allAnnotators, search, isAdmin]);

  const open = Boolean(anchor);
  const handleClose = () => { setAnchor(null); setSearch(""); };

  const doAdd = u => {
    onChange(u, true);
    setSearch("");
  };

  const doRemove = u => {
    onChange(u, false);
  };

  // display logic thresholds
  const TEXT_THRESHOLD = 2;  // 1-2 => avatar + username
  const AVATAR_ONLY_THRESHOLD = 5; // 3-5 => avatars only
  const MAX_AVATAR = 6;      // >5 => show first 5 avatars + badge

  return (
    <>
      <Box
        sx={{ display: "flex", alignItems: "center", cursor: isAdmin ? "pointer" : "default" }}
        onClick={e => {
          e.stopPropagation();
          if (isAdmin) setAnchor(e.currentTarget);
        }}
      >
        {allAnnotators.length === 0 ? (
          <Typography variant="caption" color="text.secondary">—</Typography>
        ) : allAnnotators.length <= TEXT_THRESHOLD ? (
          allAnnotators.map((u,i) => (
            <Box key={u.email} sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
              <Avatar sx={{ 
                width:20, 
                height:20, 
                fontSize:10, 
                mr:0.5,
                bgcolor: stringToColor(getUserDisplayName(u))
              }}>
                {getUserInitials(u)}
              </Avatar>
              <Typography variant="caption">{u.username || u.name}</Typography>
            </Box>
          ))
        ) : allAnnotators.length <= AVATAR_ONLY_THRESHOLD ? (
          allAnnotators.slice(0, allAnnotators.length).map((u,i) => (
            <Box key={u.email} title={u.name}>
              <Avatar
                sx={{
                  width: 20, height: 20, fontSize: 10,
                  ml: i === 0 ? 0 : -0.5,
                  zIndex: AVATAR_ONLY_THRESHOLD - i,
                  bgcolor: stringToColor(getUserDisplayName(u))
                }}
              >
                {getUserInitials(u)}
              </Avatar>
            </Box>
          ))
        ) : (
          <>  {/* >4 */}
            {allAnnotators.slice(0, MAX_AVATAR - 1).map((u,i) => (
              <Box key={u.email} title={u.name}>
                <Avatar
                  sx={{ 
                    width:20, 
                    height:20, 
                    fontSize:10, 
                    ml: i===0?0:-0.5, 
                    zIndex: MAX_AVATAR - i,
                    bgcolor: stringToColor(getUserDisplayName(u))
                  }}
                >{getUserInitials(u)}</Avatar>
              </Box>
            ))}
            <Badge
              badgeContent={`+${allAnnotators.length - (MAX_AVATAR - 1)}`} 
              color="primary"
              sx={{ ml:0.5 }}
            />
          </>
        )}
      </Box>

      <Dialog
        open={open}
        onClose={handleClose}
        onClick={e => e.stopPropagation()}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            minHeight: 500,
            bgcolor: 'white'
          }
        }}
      >
        <DialogTitle component="div" sx={{ pb: 1, bgcolor: 'white', borderBottom: '1px solid #e0e0e0' }}>
          <Typography variant="h6" component="h2" fontWeight="bold">
            Annotators
          </Typography>
        </DialogTitle>
        
        <DialogContent sx={{ p: 3, bgcolor: 'white' }}>
          {/* Parent-Level Annotators Section */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#666' }}>
              Parent-Level Annotators
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              flexWrap: 'wrap',
              gap: 1,
              mb: 2,
              minHeight: 32,
              alignItems: 'center'
            }}>
              {Array.isArray(parentLevelAnnotators) && parentLevelAnnotators.length > 0 ? (
                parentLevelAnnotators.map(u => (
                  <Chip
                    key={u.email}
                    avatar={
                      <Avatar sx={{ 
                        bgcolor: stringToColor(getUserDisplayName(u)),
                        width: 20,
                        height: 20,
                        fontSize: 10
                      }}>
                        {getUserInitials(u)}
                      </Avatar>
                    }
                    label={u.name || u.username || u.email}
                    size="small"
                    sx={{
                      bgcolor: '#f5f5f5',
                      color: '#888',
                      '& .MuiChip-label': {
                        fontSize: '12px',
                        fontWeight: 500
                      }
                    }}
                  />
                ))
              ) : (
                <Typography variant="body2" sx={{ color: '#999', fontStyle: 'italic' }}>
                  None
                </Typography>
              )}
            </Box>
          </Box>

          {/* Direct Annotators Section */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#333' }}>
              Direct Annotators
            </Typography>
            
            {/* COMMENTED OUT: Combined Direct and File-Level Annotators */}
            {/* Now only shows Direct Annotators */}
            <Box sx={{ 
              border: '1px solid #e0e0e0',
              borderRadius: 2,
              p: 1,
              minHeight: 40,
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 0.5,
              bgcolor: 'white',
              '&:focus-within': {
                borderColor: '#1976d2',
                boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.2)'
              }
            }}>
              {directAnnotators.map(u => (
                <Chip
                  key={u.email}
                  avatar={
                    <Avatar sx={{ 
                      bgcolor: stringToColor(getUserDisplayName(u)),
                      width: 20,
                      height: 20,
                      fontSize: 10
                    }}>
                      {getUserInitials(u)}
                    </Avatar>
                  }
                  label={u.name || u.username || u.email}
                  onDelete={() => doRemove(u)}
                  deleteIcon={<CloseIcon sx={{ fontSize: 16 }} />}
                  size="small"
                  sx={{
                    bgcolor: '#e3f2fd',
                    '& .MuiChip-label': {
                      fontSize: '12px',
                      fontWeight: 500,
                      paddingRight: '4px'
                    },
                    '& .MuiChip-deleteIcon': {
                      fontSize: 16,
                      margin: '0 2px 0 -4px'
                    }
                  }}
                />
              ))}
              <TextField
                placeholder={editableDirectAnnotators.length === 0 ? "Type to search and add users..." : "Type to search..."}
                value={search}
                onChange={e => setSearch(e.target.value)}
                variant="standard"
                size="small"
                sx={{
                  flex: 1,
                  minWidth: 120,
                  '& .MuiInput-underline:before': { display: 'none' },
                  '& .MuiInput-underline:after': { display: 'none' },
                  '& .MuiInputBase-input': {
                    fontSize: '14px',
                    padding: '4px 0'
                  }
                }}
              />
            </Box>
          </Box>

          {/* Available Users Section */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#333' }}>
              Available Users
            </Typography>
            <Box sx={{ 
              bgcolor: '#fafafa', 
              borderRadius: 2, 
              border: '1px solid #e0e0e0',
              maxHeight: 350,
              overflowY: 'auto'
            }}>
              {available.length === 0 ? (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    {search ? 'No users found' : 'No available users to add'}
                  </Typography>
                </Box>
              ) : (
                available.map((u, index) => (
                  <Box
                    key={u.email}
                    onClick={e => { e.stopPropagation(); doAdd(u); }}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      p: 1,
                      cursor: 'pointer',
                      borderBottom: index < available.length - 1 ? '1px solid #f0f0f0' : 'none',
                      '&:hover': {
                        bgcolor: '#f0f0f0'
                      }
                    }}
                  >
                    <Avatar sx={{ 
                      bgcolor: stringToColor(getUserDisplayName(u)),
                      width: 20,
                      height: 20,
                      fontSize: 10,
                      mr: 1.2
                    }}>
                      {getUserInitials(u)}
                    </Avatar>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontWeight: 500,
                        fontSize: '12px'
                      }}
                    >
                      {u.name || u.username || u.email}
                    </Typography>
                  </Box>
                ))
              )}
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

    </>
  );
}
